import React, { useCallback, useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { copy } from '@shakkerai/util-copy';
import { convert2CompressedUrl, getRemoteImageSize } from '@shakkerai/util-image';
import { Loader, Tooltip } from '@mantine/core';
import { Icon } from '@shakkerai/components';
import { useBoolean } from 'ahooks';
import { useElementGenInfo } from '@shakkerai/pro-hooks';
import { insertElement, moveElementToTop, updateElement } from '@starflow/app/helpers/canvas';
// import { postShareByUser } from '@shakkerai/services';
import { GenerationInfoPanelInner } from '../generator/components/_legacy/info';
import { useDownloadState } from '@shakkerai/pro-store';
import { translate } from '@shakkerai/util-i18n';
import { encode } from '@shakkerai/pro-utils';
import { notifyError, notifySuccess } from '@shakkerai/pro-components';
import { mc } from '@shakkerai/report';
import { useEventEmitter } from '@starflow/app/helpers';
import { setTempState, useUnitData } from '@starflow/app/store';
import { applyColorReferenceFromImg, findNextTaskPosition } from '@starflow/app/modules/generator/helpers';
import { applyImg2ImgSourceImage, applyReferenceImage } from '@starflow/app/modules/generator/helpers';
import { enterDerivativeFromElement } from './helpers';
import { viewportActions, useProjectState, getProjectState, removeQueue } from '@starflow/app/store';
import {
  getActiveGeneratorNodeId,
  getProcessor,
  updateUnitData,
  updateUnitResult,
  saveFlowWhenUpdate,
  resetFlowAfterNode,
  findFlowNextNodeId,
} from '@starflow/app/helpers/flow';
import { MODEL_SD35 } from '../models';
import iconSlider from './assets/slider.svg';
import imgSliderHandler from './assets/slider_handler.png';
import shareIcon from './assets/share.svg';
import infoIcon from './assets/info.svg';
import backArrow from './assets/Arrow_Right.svg';
import saveToCanvas from './assets/save_to_canvas.svg';
import cs from 'classnames';

// const sharedImages = {};
const StatusLabel = ({ taskData }) => {
  const { status, queueIndex, queueLength, progress } = taskData;

  if (status === 'queueing') {
    return (
      <span className="text-xs text-[#2d2d2d]">
        {translate('generator_task_queuing')} {queueIndex || 1}/{queueLength || 1}
      </span>
    );
  }

  if (status === 'generating') {
    return <span className="text-xs text-[#2d2d2d]">{`${Math.ceil(progress)}%`}</span>;
  }

  if (status === 'error') {
    return <span className="text-xs text-[#2d2d2d]">{translate('generator_failed_2')}</span>;
  }

  return null;
};

export const DerivativeTask = ({ data }) => {
  return (
    <div
      data-role="derivative-task-preview"
      className="absolute inset-0 flex flex-col items-center justify-center bg-white/90"
    >
      <StatusLabel taskData={data} />
    </div>
  );
};

export const DerivativeQueue = ({ data }) => {
  const handleCancel = useCallback(() => {
    removeQueue(data.queueId);
  }, [data.id]);

  return (
    <div
      data-role="derivative-queue-preview"
      className="absolute inset-0 flex flex-col items-center justify-center bg-white/90"
    >
      <button
        onClick={handleCancel}
        className="absolute top-2 right-2 flex items-center justify-center w-6 h-6 bg-black/60 rounded-full"
      >
        <Icon className="shk-prompt-lora-guanbi" fontSize={12} color="#fff" />
      </button>
      <span className="text-xs text-[#2d2d2d]">{translate('generator_task_waiting')}</span>
    </div>
  );
};

const GenerationInfoPanel = ({ image, onClose }) => {
  const genInfo = useElementGenInfo(image);
  const [position, setPosition] = useState(null);

  const { width, height } = image;

  useEffect(() => {
    const button = document.getElementById(`element-derivative-item-${image.innerImageId}`);

    if (!button) return null;

    const rect = button.getBoundingClientRect();
    const left = rect.right;
    const rightGap = window.innerWidth - left;
    const positionX = rightGap > 310 ? left : window.innerWidth - 310;

    setPosition({ x: positionX, y: 105 });
  }, [image]);

  if (!image || !position) return null;

  return createPortal(
    <Rnd
      scale={1}
      bounds={document.body}
      default={position}
      className="z-[100] imginfo-window w-[500px]"
      dragHandleClassName="sub-drag-handle"
    >
      <div
        style={{ maxHeight: window.innerHeight - 100 }}
        className="flex flex-col items-stretch w-[300px] bg-white rounded-[2px] shadow-light pointer-events-auto"
      >
        <GenerationInfoPanelInner
          image={image}
          genInfo={genInfo}
          imageWidth={width}
          imageHeight={height}
          onClose={onClose}
          noReproduce
        />
      </div>
    </Rnd>,
    document.body,
  );
};

export const SlideDiffViewer = (props) => {
  const refData = useRef({});
  const sliderWrapperRef = useRef(null);
  const resultWrapperRef = useRef(null);
  const { width, height, sourceElement, image, scale, style, className } = props;

  const handleMouseMove = (e) => {
    if (!refData.current.dragging) return;

    const dragOffset = e.clientX - sliderWrapperRef.current.getBoundingClientRect().left;
    const newWidth = Math.min(width, Math.max(0, dragOffset));
    sliderWrapperRef.current.style.width = `${newWidth}px`;
  };

  const handleMouseDown = () => {
    refData.current.dragging = true;
    resultWrapperRef.current.style.pointerEvents = 'auto';
  };

  useEffect(() => {
    if (!sliderWrapperRef.current) return;

    const prevScale = refData.current.prevScale || scale;
    const scaleDiff = scale / prevScale;

    const sliderWrapperWidth = sliderWrapperRef.current.offsetWidth;
    const newWidth = sliderWrapperWidth * scaleDiff;

    sliderWrapperRef.current.style.width = newWidth + 'px';
    refData.current.prevScale = scale;
  }, [scale]);

  useEffect(() => {
    sliderWrapperRef.current.style.width = `${width * 0.8}px`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleMouseUp = () => {
      refData.current.dragging = false;
      resultWrapperRef.current.style.pointerEvents = 'none';
    };

    document.body.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={resultWrapperRef}
      style={style}
      data-hidden="mini-mode"
      onMouseMove={handleMouseMove}
      className={`absolute z-30 ${className}`}
    >
      <div style={{ width, height }} className="flex items-stretch absolute z-10 inset-0">
        <img style={{ width, height }} className="absolute top-0 left-0" src={sourceElement.url} />
        <div ref={sliderWrapperRef} className="relative overflow-hidden">
          <img style={{ maxWidth: 'unset', width, height }} className="absolute top-0 left-0" src={image.url} />
        </div>
        <div className="relative z-10 flex flex-shrink-0 flex-col items-center justify-center w-px bg-white">
          <img
            draggable="false"
            src={imgSliderHandler}
            onMouseDown={handleMouseDown}
            className="max-w-none w-7 h-7 pointer-events-auto cursor-col-resize"
          />
        </div>
        <div className="relative flex-1 overflow-hidden">
          <span className="absolute top-2 right-2 px-1.5 py-1 leading-3 bg-black/75 rounded-sm text-xs whitespace-nowrap text-white">
            <span className="translate-y-1">{translate('img_bottom_tool_original_image')}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export const HoverDiffViewer = (props) => {
  const resultWrapperRef = useRef(null);
  const { width, height, sourceElement, image, style, className } = props;

  return (
    <div data-hidden="mini-mode" ref={resultWrapperRef} style={style} className={`absolute z-30 ${className}`}>
      <div style={{ width, height }} className="flex items-stretch absolute z-10 inset-0">
        <img style={{ width, height }} className="absolute top-0 left-0" src={sourceElement.url} />
        <button className="peer absolute z-[11] right-3 bottom-3 px-2 h-6 flex items-center justify-center bg-black/50 border border-white/30 pointer-events-auto rounded">
          <img src={iconSlider} alt="" />
          <span className="ml-1 text-xs text-white">{translate('img_bottom_tool_view_diff')}</span>
        </button>
        <img
          draggable={false}
          style={{
            maxWidth: 'unset',
            width,
            height,
          }}
          className="absolute top-0 left-0 opacity-100 peer-hover:opacity-0 pointer-events-auto"
          src={image.url}
        />
      </div>
    </div>
  );
};

export const DerivativeResultActions = ({ data, onCancel, title = translate('originalimage') }) => {
  const { downloadImageWithConfirm } = useDownloadState();
  const [genInfoVisible, genInfoVisibleActions] = useBoolean();

  const handleDownloadImage = useCallback(() => {
    downloadImageWithConfirm(data.url, data.innerImageId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, downloadImageWithConfirm]);

  const handleShareImage = useCallback(() => {
    const params = `g=${data?.innerTaskId}&i=${data.innerImageId}`;
    const url = `${window.location.origin}/aigenerator/share?p=${encode(params)}`;

    mc('tool.canvas.share', { image_id: data.innerImageId, from: 'sidebar' });

    copy(url).then(
      () => {
        notifySuccess(translate('img_link_copied'));
      },
      () => {
        notifyError(`Copy failed`);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleSendToCanvas = async () => {
    const { url, innerImageId, unitId, innerTaskId } = data;
    let { width, height } = data;

    if (!width || !height) {
      try {
        const size = await getRemoteImageSize(url);
        width = size.width;
        height = size.height;
      } catch {}
    }

    const { x, y } = findNextTaskPosition();

    const elementId = insertElement({
      type: 'image',
      x,
      y,
      width,
      height,
      innerTaskId,
      innerImageId,
      unitId,
      originalTaskId: innerTaskId,
      originalImageId: innerImageId,
      originalUnitId: unitId,
      url: url,
      originalUrl: url,
      data: data.data,
    });

    viewportActions.locateElement(elementId, { animate: true });
  };

  const actionDisabled = !data.url;

  return (
    <>
      <button onClick={onCancel} className="flex items-center h-8 pl-4 pr-6  bg-white shadow-light rounded-sm">
        {/*这里的“返回”文案似乎是根据不同的衍生工具类型而不同，看看能不能PK成统一的，不然又得加配置项了，烦死😡*/}
        {/* 已pk，文案改成原图（lijunchen） */}
        <img src={backArrow} alt="" />
        <span className="text-xs text-[#2d2d2d] ml-[4px]">{title}</span>
      </button>
      <div className="flex items-center justify-center h-8 ml-5 bg-white shadow-light rounded-sm">
        <Tooltip withArrow position="bottom" className="scale-90" label={translate('download')}>
          <button
            disabled={actionDisabled}
            onClick={handleDownloadImage}
            className="flex items-center w-[22px] h-[32px] px-1 ml-3 disabled:opacity-30"
          >
            <Icon fontSize={16} className="shk-download" />
          </button>
        </Tooltip>

        <Tooltip withArrow position="bottom" className="scale-90" label={translate('image_share')}>
          <button
            disabled={actionDisabled}
            onClick={handleShareImage}
            className="flex items-center w-[22px] h-[32px] px-1 ml-1.5 disabled:opacity-30"
          >
            {/* <span className="text-xs text-[#2d2d2d]">分享</span> */}
            <img className="w-[16px] h-[16px]" src={shareIcon} alt="" />
          </button>
        </Tooltip>

        <Tooltip withArrow position="bottom" className="scale-90" label={translate('menu_save_to_canvas')}>
          <button
            disabled={actionDisabled}
            onClick={handleSendToCanvas}
            className="flex items-center w-[22px] h-[32px] px-1 ml-2 disabled:opacity-30"
          >
            {/* <span className="text-xs text-[#2d2d2d]">保存到画布</span> */}
            <img className="w-[16px] h-[16px]" src={saveToCanvas} alt="" />
          </button>
        </Tooltip>

        <div className="w-px h-5 bg-[#ccc] ml-3" />
        <Tooltip withArrow position="bottom" className="scale-90" label={translate('generation_info')}>
          <button
            id={`element-derivative-item-${data.innerImageId}`}
            disabled={actionDisabled}
            onClick={genInfoVisibleActions.setTrue}
            className="flex items-center w-[22px] h-[32px] px-1 mr-3 ml-1.5 disabled:opacity-30"
          >
            {/* <span className="text-xs text-[#2d2d2d]">信息</span> */}
            <img className="w-[16px] h-[16px]" src={infoIcon} alt="" />
          </button>
        </Tooltip>
      </div>
      {genInfoVisible && <GenerationInfoPanel image={data} onClose={genInfoVisibleActions.setFalse} />}
    </>
  );
};

export const DerivativeResults = React.memo(
  forwardRef(
    (
      { elementId, sourceElement, selected, onSelect, items, className, isGenerator = false, flowId, currentNodeId },
      ref,
    ) => {
      // generator没有sourceElement, 取results的最后一张图, 所以真实的task要过滤最后一项
      const tasks = isGenerator ? items.slice(0, items.length - 1) : items;
      const isMounted = useRef(false);

      const handleCancelQueue = useCallback((queueId) => {
        removeQueue(queueId);
      }, []);

      const handleSelect = useCallback(
        (index, data) => {
          onSelect(index);
          // 更新用户选择的索引
          updateUnitResult(currentNodeId, () => ({
            selectedIndex: index,
          }));

          // 选中原图，则恢复到原图
          if (index === -1) {
            updateElement(elementId, {
              url: sourceElement.url,
              innerTaskId: sourceElement.originalTaskId,
              innerImageId: sourceElement.originalImageId,
              unitId: sourceElement.unitId,
              width: sourceElement.width,
              height: sourceElement.height,
              data: sourceElement.data,
            });

            // 先简单处理一版本
            // 生成器选原图时，更新流节点图片, 其他节点选原图, 清掉流节点图片
            updateUnitResult(currentNodeId, () => ({
              url: isGenerator ? sourceElement.url : '',
            }));
            resetFlowAfterNode(flowId, currentNodeId);
            // 更新下一个节点的sourceElement
            const nextNodeId = findFlowNextNodeId(flowId, currentNodeId);
            // 只有生成器选原图时，才更新下一个节点的sourceElement
            if (isGenerator && nextNodeId) {
              updateUnitData(nextNodeId, {
                sourceElement: {
                  ...sourceElement,
                  // 这三个属性先临时加一下, 后面问问刚师傅怎么处理
                  scaleX: sourceElement.scaleX || 1,
                  scaleY: sourceElement.scaleY || 1,
                  type: 'image',
                },
              });
            }
            // 保存流
            if (flowId) {
              saveFlowWhenUpdate(flowId);
            }
            return;
          }

          if (data && data.url) {
            let nextSourceElement = null;
            // 选中结果图，则更新到结果图
            updateElement(
              elementId,
              (element) => {
                const nextElement = {
                  url: data.url,
                  innerTaskId: data.innerTaskId,
                  innerImageId: data.innerImageId,
                  unitId: data.unitId,
                  data: data.data,
                  width: data.width || element.width,
                  height: data.height || element.height,
                  scaleX: 1,
                  scaleY: 1,
                  type: 'image',
                };

                if (!element.originalTaskId) {
                  nextElement.originalUrl = element.originalUrl || element.url;
                  nextElement.originalUnitId = element.unitId;
                  nextElement.originalTaskId = element.innerTaskId;
                  nextElement.originalImageId = element.innerImageId;
                  nextElement.originalData = element.data;
                  nextElement.originalWidth = element.width;
                  nextElement.originalHeight = element.height;
                }

                nextSourceElement = nextElement;

                return nextElement;
              },
              { skipTracking: true },
            );

            moveElementToTop(elementId);

            // 更新当前节点的图片
            updateUnitResult(currentNodeId, () => ({
              url: data.url,
            }));

            // 清除当前节点之后所有节点的数据
            resetFlowAfterNode(flowId, currentNodeId);

            // 更新下一个节点的sourceElement
            const nextNodeId = findFlowNextNodeId(flowId, currentNodeId);

            if (nextNodeId) {
              updateUnitData(nextNodeId, {
                sourceElement: { ...nextSourceElement },
              });
            }
            // 保存流
            if (flowId) {
              saveFlowWhenUpdate(flowId);
            }
          }
        },
        [elementId],
      );

      // 初始化后, 当有新的生图任务时, 更新指示器
      useEffect(() => {
        if (!isMounted.current) {
          isMounted.current = true;
        } else {
          handleSelect(0);
        }
      }, [tasks.length]);

      // 当生图任务结束时, 选第一个结果
      useEventEmitter(
        `task_finish_${currentNodeId}`,
        ({ results }) => {
          if (results?.length > 0) {
            handleSelect(0, results[0]);
          }
        },
        [],
      );

      useImperativeHandle(ref, () => ({
        handleSelect,
      }));

      if (!tasks.length) {
        return null;
      }

      return (
        <aside
          data-proxy-wheel="false"
          data-role="derivative-results-thumbs"
          className={cs(
            'absolute top-0 right-full max-h-[288px] overflow-auto p-[7px] shadow-light rounded-l-lg bg-white pointer-events-auto tiny-scrollbar',
            className,
          )}
        >
          <div className="flex flex-wrap w-[150px] p-1">
            <button
              disabled={selected === -1}
              onClick={() => handleSelect(-1)}
              className="relative flex items-center justify-center size-[68px] m-px border-2 border-transparent disabled:border-primary rounded"
            >
              <img
                src={convert2CompressedUrl(sourceElement?.url, 144)}
                className="size-[60px] object-cover rounded-sm"
              />
              <span className="absolute top-0.5 right-0.5 px-1 py-px text-white text-[10px] bg-black/60 rounded-sm">
                {translate('original')}
              </span>
            </button>
            {tasks.map((task, index) => (
              <div key={task.id} className="relative">
                <button
                  disabled={selected === index}
                  id={`derivative-result-${task.id}`}
                  onClick={() => handleSelect(index, task)}
                  className="flex items-center justify-center size-[68px] m-px border-2 border-transparent disabled:border-primary rounded"
                >
                  {task.url ? (
                    <img
                      src={convert2CompressedUrl(task.url, 144)}
                      className="block size-[60px] object-cover rounded-sm"
                    />
                  ) : (
                    <div className="flex items-center justify-center size-[60px] bg-[#f4f6f8] rounded-sm">
                      {task.type === 'task' ? (
                        <Loader size={12} />
                      ) : (
                        <span className="text-[10px] text-[#2d2d2d]">{translate('generator_task_waiting')}</span>
                      )}
                    </div>
                  )}
                </button>
                {task.type === 'queue' && (
                  <button
                    onClick={() => handleCancelQueue(task.queueId)}
                    className="absolute top-2 right-2 flex items-center justify-center w-4 h-4 bg-black/60 rounded-full"
                  >
                    <Icon className="shk-prompt-lora-guanbi" fontSize={8} color="#fff" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </aside>
      );
    },
  ),
);

export const DerivativedImageToolbar = React.memo((props) => {
  const { type, element } = props;
  const processor = getProcessor(type);
  const { baseType, presetBaseModelId } = useUnitData();

  const handleEnterDerivative = useCallback(() => {
    enterDerivativeFromElement(type, element.id);
  }, []);

  const handleSendToReference = useCallback(() => {
    mc('tool.canvas.reference', { image_id: element.innerImageId });
    applyReferenceImage(element);
  }, [element]);

  const handleSendToColorReference = useCallback(() => {
    applyColorReferenceFromImg(element);
  }, [element]);

  const handleSendToImg2Img = useCallback(
    (event) => {
      const generatorId = getActiveGeneratorNodeId();
      mc('tool.canvas.img2img', { image_id: element.innerImageId });
      applyImg2ImgSourceImage(element, generatorId);
      setTempState('exclusiveWindow', `image-input-options-${generatorId}`);

      event.stopPropagation();
    },
    [element],
  );

  // SD35模型不支持作为参考图
  const disableReference = baseType === MODEL_SD35;
  // 仅支持星流
  const disableColorReference = presetBaseModelId !== 'base_checkpoint';

  return (
    <div className="absolute z-1 top-full left-1/2 -translate-x-1/2 w-auto flex items-center whitespace-nowrap text-xs">
      <div className="flex items-center h-8 px-4 bg-white shadow-light rounded-sm">
        <span className="text-xs text-[#2d2d2d] font-semibold">{processor.title}</span>
        <button onClick={handleEnterDerivative} className="ml-3 px-2 py-1 bg-[#f2f4f6] text-xs text-[#2d2d2d]">
          {translate('img_bottom_bar_edit')}
        </button>
        <div className="w-px h-4 mx-3 bg-[#cdcdcd]" />
        <span className="text-xs text-[#2d2d2d] font-semibold">{translate('img_bottom_bar_use')}</span>
        <div className={`group relative flex items-center h-6 px-2 cursor-pointer ml-3 bg-[#f2f4f6]`}>
          {translate('image_tool_use_image')}
          <Icon className="shk-kuangjia-xiala -mr-2" color="#666" fontSize={18} />
          <div className="absolute hidden group-hover:block left-0 top-[24px] flex flex-col items-stretch rounded-sm bg-[#2c2c2c] py-1.5 px-0">
            <button
              onClick={handleSendToImg2Img}
              className="flex flex-col w-full px-3 py-1.5 text-white hover:bg-primary hover:text-[#2c2c2c]"
            >
              <span className="text-xs leading-[16px] font-semibold">{translate('img_use_as_i2i_title')}</span>
              <span className="mt-1 leading-[12px] text-[11px]">{translate('img_use_as_i2i_desc')}</span>
            </button>
            <button
              disabled={disableReference}
              onClick={handleSendToReference}
              className="flex flex-col w-full px-3 py-1.5 text-white hover:bg-primary hover:text-[#2c2c2c] disabled:pointer-events-none disabled:opacity-30"
            >
              <span className="text-xs leading-[16px] font-semibold">{translate('img_use_as_ref_title')}</span>
              <span className="mt-1 leading-[12px] text-[11px]">{translate('img_use_as_ref_desc')}</span>
            </button>
            <button
              disabled={disableColorReference}
              onClick={handleSendToColorReference}
              className="flex flex-col w-full px-3 py-1.5 text-white hover:bg-primary hover:text-[#2c2c2c] disabled:pointer-events-none disabled:opacity-30"
            >
              <span className="text-xs leading-[16px] font-semibold">{translate('color_canvas_para1')}</span>
              <span className="mt-1 leading-[12px] text-[11px]">{translate('color_canvas_para2')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export const FocusHandler = React.memo(() => {
  const selection = useProjectState('selection');
  const editingElementId = useProjectState('editingElementId');

  useEffect(() => {
    if (selection.length === 1 && editingElementId !== selection[0]) {
      const tasks = getProjectState().tasks;
      const currentTask = tasks.map[tasks.current];

      if (currentTask && currentTask.params?.sourceElement?.id === selection[0]) {
        enterDerivativeFromElement(currentTask.type, selection[0]);
      }

      // 选中耽搁元素的时候
    }
  }, [selection, editingElementId]);

  return null;
});

export const wrapImageOverlayer = (Overlayer) => {
  return (props) => {
    const { element, currentElementTool, isEditing, isTaskRunning, isExactlySelected, taskData } = props;

    let toolbar = props.toolbar;

    const lastDerivativeType = element.lastDerivativeUnitId?.split('-')?.[1];
    if (lastDerivativeType && (currentElementTool === null || lastDerivativeType === currentElementTool)) {
      toolbar = <DerivativedImageToolbar {...props} type={lastDerivativeType} />;
    }

    useEffect(() => {
      // 一旦进入编辑状态，就清掉标题
      updateElement(element.id, { topTitle: null }, { skipTracking: true });
    }, [isEditing]);

    if (isTaskRunning || element.topTitle) {
      if (!isEditing) {
        return (
          <>
            <div className="absolute -top-5 left-0 flex items-center h-4 text-primary whitespace-nowrap text-[10px]">
              <div className="w-2 h-2 border border-primary rotate-45 mr-1" />
              {element.topTitle ? (
                <span>{element.topTitle}</span>
              ) : (
                <span>
                  {taskData.params.unitLabel} - {taskData.progress}%
                </span>
              )}
            </div>
            {isExactlySelected && <Overlayer {...props} toolbar={toolbar} />}
          </>
        );
      } else {
        return null;
      }
    }

    return <Overlayer {...props} toolbar={toolbar} />;
  };
};
